import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Sparkles, Trash2, Save } from 'lucide-react';
import {
  legacyUformMappingApi,
  type LegacyUformMappingsResponse,
  type UformOption,
  type LegacyUformMappingDto,
  type LegacyUformDto,
} from '@/lib/api';
import { useStandort } from '@/components/layouts/use-standort';

interface RowState {
  legacyId: number;
  legacyName: string;
  legacyDauer: number | null;
  selectedNewUformId: number | null;
  persistedNewUformId: number | null;
  dirty: boolean;
}

const buildRows = (data: LegacyUformMappingsResponse | null): RowState[] => {
  if (!data) return [];
  const byLegacy = new Map<number, LegacyUformMappingDto>();
  for (const m of data.mappings) byLegacy.set(m.legacy_id, m);

  return data.legacy_uforms
    .slice()
    .sort((a, b) => a.id - b.id)
    .map((u: LegacyUformDto) => {
      const existing = byLegacy.get(u.id);
      return {
        legacyId: u.id,
        legacyName: u.name,
        legacyDauer: u.dauer,
        selectedNewUformId: existing ? existing.new_uform_id : null,
        persistedNewUformId: existing ? existing.new_uform_id : null,
        dirty: false,
      };
    });
};

const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

const findBestNameMatch = (legacyName: string, legacyDauer: number | null, options: UformOption[]): UformOption | null => {
  const target = normalize(legacyName);
  if (!target) return null;
  // Bevorzugt: Name + (Dauer-Hinweis) → z. B. "Einzelunterricht 30" matched "Einzel 30 Min."
  const dauerTokens = legacyDauer && legacyDauer > 0 ? [String(legacyDauer)] : [];
  const score = (opt: UformOption): number => {
    const cand = normalize(opt.name);
    let s = 0;
    if (cand === target) s += 100;
    else if (cand.startsWith(target) || target.startsWith(cand)) s += 60;
    else if (cand.includes(target) || target.includes(cand)) s += 30;
    for (const tok of dauerTokens) {
      if (cand.includes(tok)) s += 20;
    }
    return s;
  };
  let best: UformOption | null = null;
  let bestScore = 0;
  for (const opt of options) {
    const s = score(opt);
    if (s > bestScore) {
      best = opt;
      bestScore = s;
    }
  }
  return bestScore >= 30 ? best : null;
};

const LegacyUformMappingsPage: React.FC = () => {
  const { standorte, selectedStandortId } = useStandort();
  const [activeStandortId, setActiveStandortId] = useState<number | null>(
    selectedStandortId ? Number(selectedStandortId) : null
  );
  const [data, setData] = useState<LegacyUformMappingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Sidebar-Wechsel des Standorts übernehmen.
  useEffect(() => {
    if (selectedStandortId) {
      const id = Number(selectedStandortId);
      if (Number.isFinite(id) && id > 0) setActiveStandortId(id);
    }
  }, [selectedStandortId]);

  const load = useCallback(async () => {
    if (!activeStandortId || activeStandortId <= 0) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const res = await legacyUformMappingApi.list(activeStandortId);
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [activeStandortId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setRows(buildRows(data));
  }, [data]);

  const uformOptions = data?.uforme ?? [];
  const activeUformOptions = useMemo(() => uformOptions.filter((u) => u.aktiv), [uformOptions]);
  const uformById = useMemo(() => {
    const map = new Map<number, UformOption>();
    uformOptions.forEach((u) => map.set(u.id, u));
    return map;
  }, [uformOptions]);

  const dirtyCount = rows.filter((r) => r.dirty).length;
  const unmappedCount = rows.filter((r) => r.selectedNewUformId === null).length;

  const handleSelectChange = (legacyId: number, valueRaw: string) => {
    const value = valueRaw === '__unset__' ? null : Number(valueRaw);
    setRows((prev) => prev.map((r) => {
      if (r.legacyId !== legacyId) return r;
      const next = value !== null && Number.isFinite(value) ? value : null;
      return {
        ...r,
        selectedNewUformId: next,
        dirty: next !== r.persistedNewUformId,
      };
    }));
  };

  const handleSaveRow = async (row: RowState) => {
    if (!activeStandortId) return;
    if (row.selectedNewUformId === null) {
      if (row.persistedNewUformId === null) return;
      setSavingId(row.legacyId);
      try {
        await legacyUformMappingApi.delete(activeStandortId, row.legacyId);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
      } finally {
        setSavingId(null);
      }
      return;
    }
    setSavingId(row.legacyId);
    try {
      await legacyUformMappingApi.upsert({
        standort_id: activeStandortId,
        legacy_uform_id: row.legacyId,
        new_uform_id: row.selectedNewUformId,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  const handleAutoSuggest = () => {
    setNotice(null);
    setRows((prev) => {
      let filled = 0;
      const next = prev.map((r) => {
        if (r.selectedNewUformId !== null) return r;
        const match = findBestNameMatch(r.legacyName, r.legacyDauer, activeUformOptions);
        if (!match) return r;
        filled += 1;
        return {
          ...r,
          selectedNewUformId: match.id,
          dirty: match.id !== r.persistedNewUformId,
        };
      });
      setNotice(`${filled} Vorschläge eingefügt. Bitte prüfen und „Alle Änderungen speichern".`);
      return next;
    });
  };

  const handleBulkSave = async () => {
    if (!activeStandortId) return;
    const toWrite = rows.filter((r) => r.dirty && r.selectedNewUformId !== null);
    if (toWrite.length === 0) return;
    setBulkSaving(true);
    setError(null);
    try {
      const res = await legacyUformMappingApi.bulk({
        standort_id: activeStandortId,
        mappings: toWrite.map((r) => ({
          legacy_uform_id: r.legacyId,
          new_uform_id: r.selectedNewUformId as number,
        })),
      });
      await load();
      setNotice(`${res.written} Mapping(s) gespeichert${res.skipped > 0 ? `, ${res.skipped} übersprungen` : ''}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk-Speichern fehlgeschlagen');
    } finally {
      setBulkSaving(false);
    }
  };

  const standortName = activeStandortId
    ? standorte.find((s) => Number(s.id) === activeStandortId)?.name ?? `#${activeStandortId}`
    : null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Legacy-Unterrichtsform-Mappings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ordnet die Legacy-Unterrichtsform-IDs aus dem Alt-System
            (<code>_fim_unterrichtsformen</code>) der neuen System-Unterrichtsform-ID zu.
            Mappings sind <strong>pro Standort</strong>, weil die Legacy-IDs nicht
            standortübergreifend stabil sind. Wenn der Office im Migrations-Wizard
            keinen Wert manuell setzt, wird automatisch der hier hinterlegte
            Mapping-Wert vorbefüllt.
          </p>
        </div>
        <div className="min-w-[260px]">
          <Select
            value={activeStandortId ? String(activeStandortId) : ''}
            onValueChange={(v) => {
              const n = Number(v);
              if (Number.isFinite(n) && n > 0) setActiveStandortId(n);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Standort wählen" />
            </SelectTrigger>
            <SelectContent>
              {standorte.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              Mappings
              {standortName ? <span className="ml-2 text-xs font-normal text-muted-foreground">· {standortName}</span> : null}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleAutoSuggest}
                disabled={loading || activeUformOptions.length === 0 || rows.length === 0}
              >
                <Sparkles className="mr-2 h-4 w-4" /> Auto-Vorschläge generieren
              </Button>
              <Button
                type="button"
                onClick={handleBulkSave}
                disabled={dirtyCount === 0 || bulkSaving}
              >
                {bulkSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Alle Änderungen speichern{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-xs text-muted-foreground">
            {rows.length} Legacy-Unterrichtsformen · {unmappedCount} unmapped · {dirtyCount} geänderte Zeilen
          </div>

          {error ? (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              {error}
            </div>
          ) : null}
          {notice ? (
            <div className="rounded-md border border-primary/40 bg-primary/10 p-3 text-sm text-primary-foreground">
              {notice}
            </div>
          ) : null}

          {!activeStandortId ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Bitte zuerst einen Standort wählen.
            </div>
          ) : loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Lade Mappings…
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Keine Legacy-Unterrichtsformen aus <code>_fim_unterrichtsformen</code> geladen.
              Prüfe die Legacy-DB-Verbindung des Standorts.
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-border/70">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Legacy-ID</th>
                    <th className="px-3 py-2 text-left">Legacy-Name (Alt-System)</th>
                    <th className="px-3 py-2 text-left">Dauer</th>
                    <th className="px-3 py-2 text-left">Neue Unterrichtsform</th>
                    <th className="px-3 py-2 text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const persistedName = row.persistedNewUformId
                      ? uformById.get(row.persistedNewUformId)?.name
                      : null;
                    return (
                      <tr
                        key={row.legacyId}
                        className={`border-t border-border/40 ${row.dirty ? 'bg-amber-500/5' : ''}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{row.legacyId}</td>
                        <td className="px-3 py-2">{row.legacyName || <span className="text-muted-foreground italic">—</span>}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {row.legacyDauer && row.legacyDauer > 0 ? `${row.legacyDauer} Min.` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Select
                              value={row.selectedNewUformId !== null ? String(row.selectedNewUformId) : '__unset__'}
                              onValueChange={(v) => handleSelectChange(row.legacyId, v)}
                            >
                              <SelectTrigger className="min-w-[260px]">
                                <SelectValue placeholder="— wählen —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__unset__">— kein Mapping —</SelectItem>
                                {uformOptions.map((u) => (
                                  <SelectItem key={u.id} value={String(u.id)}>
                                    {u.name}{!u.aktiv ? ' (inaktiv)' : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {row.dirty && row.persistedNewUformId !== null ? (
                              <span className="text-xs text-muted-foreground">
                                vorher: {persistedName ?? `#${row.persistedNewUformId}`}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={row.dirty ? 'default' : 'outline'}
                              disabled={!row.dirty || savingId === row.legacyId}
                              onClick={() => handleSaveRow(row)}
                            >
                              {savingId === row.legacyId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Save className="h-3.5 w-3.5" />
                              )}
                            </Button>
                            {row.persistedNewUformId !== null ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={savingId === row.legacyId}
                                onClick={async () => {
                                  if (!activeStandortId) return;
                                  if (!confirm(`Mapping für „${row.legacyName}" wirklich löschen?`)) return;
                                  setSavingId(row.legacyId);
                                  try {
                                    await legacyUformMappingApi.delete(activeStandortId, row.legacyId);
                                    await load();
                                  } catch (e) {
                                    setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
                                  } finally {
                                    setSavingId(null);
                                  }
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LegacyUformMappingsPage;
